import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { listThreadHostOffers, offerFork, offerHostRole, withdrawHostOffer } from "../../../api/threads/threadHostingApi";
import type { HostOffer } from "../../../api/threads/threadDecoders";
import { appPaths } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import CopyButton from "../../../ui/content/CopyButton";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import ModalShell from "../../../ui/overlays/ModalShell";
import { FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { useThreadContext } from "../page/threadPageContext";
import Field from "../../../ui/forms/Field";
import ChoiceCard from "../../../ui/forms/ChoiceCard";
import EmptyState from "../../../ui/feedback/EmptyState";

/** A project member the host may hand the Thread to. */
export interface MemberCandidate {
  readonly userId: string;
  readonly displayName: string;
}

interface HandOffDialogProps {
  members: readonly MemberCandidate[];
  lastEntryNumber: number;
  onClose: () => void;
}

type HandOffKind = "hostRole" | "fork";

/**
 * Hand-off, both ways (FR-THREAD-6, T-9): pass the host role to a participant, who finds it
 * in their Threads inbox (Q-60) and accepts with their own connector, or offer them a fork
 * at the latest entry. The host's pending offers are listed, so one can be withdrawn later.
 */
export default function HandOffDialog({ members, lastEntryNumber, onClose }: HandOffDialogProps) {
  const { t, i18n } = useTranslation();
  const { threadId, currentUserId } = useThreadContext();
  const candidates = members.filter((member) => member.userId !== currentUserId);
  const [recipient, setRecipient] = useState(candidates[0]?.userId ?? "");
  const [kind, setKind] = useState<HandOffKind>("hostRole");
  const [offer, setOffer] = useState<HostOffer | null>(null);
  const [forkOffered, setForkOffered] = useState(false);
  const { state, submit } = useSubmission();
  const busy = state.status === "submitting";
  const loadPending = useCallback((signal: AbortSignal) => listThreadHostOffers(threadId, signal), [threadId]);
  const pending = useLoad(loadPending);
  const nameOf = (userId: string) => members.find((member) => member.userId === userId)?.displayName ?? userId.slice(-6);

  const send = () => {
    submit(async () => {
      if (kind === "hostRole") {
        setOffer(await offerHostRole(threadId, recipient));
      } else {
        await offerFork(threadId, recipient, lastEntryNumber);
        setForkOffered(true);
      }
    });
  };

  const withdraw = (current: HostOffer) => {
    submit(async () => {
      const withdrawn = await withdrawHostOffer(current.offerId);
      if (offer?.offerId === current.offerId) {
        setOffer(withdrawn);
      }
      pending.refresh();
    });
  };
  const earlierOffers = pending.state.status === "loaded" ? pending.state.value.filter((pendingOffer) => pendingOffer.offerId !== offer?.offerId) : [];

  const offerLink = offer === null ? null : new URL(appPaths.threadHostOffer(threadId, offer.offerId), window.location.origin).toString();

  return (
    <ModalShell title={t("threads.handOff.title")} onClose={onClose} size="lg">
      <div className="space-y-4">
        {candidates.length === 0 ? <EmptyState size="small">{t("threads.handOff.noMembers")}</EmptyState> : null}
        {offer === null && !forkOffered && candidates.length > 0 ? (
          <>
            <Field label={t("threads.handOff.recipient")}>
              {(control) => (
                <select {...control} className={FORM_SELECT_CLASS} value={recipient} onChange={(event) => { setRecipient(event.target.value); }}>
                  {candidates.map((member) => (
                    <option key={member.userId} value={member.userId}>{member.displayName}</option>
                  ))}
                </select>
              )}
            </Field>
            <fieldset className="space-y-2">
              {(["hostRole", "fork"] as const).map((option) => (
                <ChoiceCard
                  key={option}
                  name="hand-off"
                  checked={kind === option}
                  onChoose={() => { setKind(option); }}
                  title={t(`threads.handOff.kinds.${option}.title`, { entry: lastEntryNumber })}
                  description={t(`threads.handOff.kinds.${option}.description`)}
                />
              ))}
            </fieldset>
            <div className="flex justify-end gap-2">
              <AppButton variant="secondary" size="sm" onClick={onClose} disabled={busy}>{t("common.cancel")}</AppButton>
              <AppButton variant="primary" size="sm" onClick={send} disabled={busy || recipient === ""}>{t("threads.handOff.send")}</AppButton>
            </div>
          </>
        ) : null}
        {forkOffered ? <StatusMessage tone="success" title={t("threads.handOff.forkOfferedTitle")}>{t("threads.handOff.forkOfferedBody")}</StatusMessage> : null}
        {offer !== null && offerLink !== null ? (
          <StatusMessage tone={offer.state === "pending" ? "info" : "warning"} title={t(`threads.handOff.offerStates.${offer.state}`)}>
            <p>{t("threads.handOff.offerLinkHint", { recipient: nameOf(offer.toUser), expires: formatDateTime(offer.expiresAt, i18n.resolvedLanguage) })}</p>
            <code className="app-code-line mt-2 block break-all px-2 py-1 font-mono text-xs">{offerLink}</code>
            <div className="mt-2 flex gap-2">
              <CopyButton value={offerLink}>{t("threads.handOff.copyLink")}</CopyButton>
              {offer.state === "pending" ? (
                <AppButton variant="danger" size="xs" disabled={busy} onClick={() => { withdraw(offer); }}>{t("threads.handOff.withdraw")}</AppButton>
              ) : null}
            </div>
          </StatusMessage>
        ) : null}
        {earlierOffers.length === 0 ? null : (
          <section className="space-y-2" aria-label={t("threads.handOff.pendingTitle")}>
            <h3 className="app-title text-sm font-semibold">{t("threads.handOff.pendingTitle")}</h3>
            <ul className="space-y-1.5">
              {earlierOffers.map((pendingOffer) => (
                <li key={pendingOffer.offerId} className="flex items-center gap-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">
                    {t("threads.handOff.pendingOffer", { recipient: nameOf(pendingOffer.toUser), expires: formatDateTime(pendingOffer.expiresAt, i18n.resolvedLanguage) })}
                  </span>
                  <AppButton variant="danger" size="xs" disabled={busy} onClick={() => { withdraw(pendingOffer); }}>{t("threads.handOff.withdraw")}</AppButton>
                </li>
              ))}
            </ul>
          </section>
        )}
        {pending.state.status === "failed" ? <ErrorNotice error={pending.state.error} onRetry={pending.reload} compact={true} /> : null}
        {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      </div>
    </ModalShell>
  );
}
